import type { TFunction } from 'i18next'
import type { OptionalModuleFlags } from '@/shared/lib/optionalModuleNav'
import { isOptionalModulePathVisible } from '@/shared/lib/optionalModuleNav'
import type { PortalNavigationItem } from '@/shared/schemas/brand.schema'

const MODULE_FLAG_KEYS = {
  lottery: 'lottery',
  'match-event': 'match_event',
  'group-trip': 'group_trip',
  listing: 'listing',
  blog: 'blog',
  'partner-offer': 'partner_offer',
  colony: 'colony',
} as const

function isModuleVisible(item: PortalNavigationItem, flags: OptionalModuleFlags): boolean {
  const moduleKey = item.module_slug === undefined ? undefined : MODULE_FLAG_KEYS[item.module_slug as keyof typeof MODULE_FLAG_KEYS]
  return moduleKey === undefined ? isOptionalModulePathVisible(item.href, flags) : flags[moduleKey]
}

function link(
  id: string,
  label: string,
  href: string,
  moduleSlug: string,
  sort: number,
): PortalNavigationItem {
  return {
    id,
    label,
    href,
    module_slug: moduleSlug,
    sort,
    enabled: true,
    link_type: 'internal',
    placement: 'header_primary',
    children: [],
  }
}

export function defaultPortalNavigation(t: TFunction): PortalNavigationItem[] {
  return [
    link('nav-home', t('app.layout.nav_home'), '/', 'shared', 10),
    {
      id: 'nav-stays',
      label: t('app.layout.nav_stays'),
      href: '',
      sort: 20,
      enabled: true,
      link_type: 'group',
      placement: 'header_primary',
      children: [
        link('nav-properties', t('app.layout.nav_properties'), '/reservation/properties', 'reservation', 10),
        link('nav-group-trips', t('app.layout.nav_group_trips'), '/group-trips', 'group-trip', 20),
        link('nav-colony', t('app.layout.nav_colony'), '/colony', 'colony', 30),
      ],
    },
    {
      id: 'nav-benefits',
      label: t('app.layout.nav_benefits'),
      href: '',
      sort: 30,
      enabled: true,
      link_type: 'group',
      placement: 'header_primary',
      children: [
        link('nav-ticketing', t('app.layout.nav_ticketing'), '/ticketing', 'ticketing', 10),
        link('nav-lottery', t('app.layout.nav_lottery'), '/lottery', 'lottery', 20),
        link('nav-match-event', t('app.layout.nav_match_event'), '/match-event', 'match-event', 30),
        link('nav-partner-offers', t('app.layout.nav_partner_offers'), '/partner-offers', 'partner-offer', 40),
        link('nav-listings', t('app.layout.nav_listings'), '/listings', 'listing', 50),
      ],
    },
    link('nav-blog', t('app.layout.nav_blog'), '/blog', 'shared', 40),
    link('nav-profile', t('app.layout.nav_profile'), '/me', 'auth', 90),
  ]
}

export function projectPortalNavigation(
  items: PortalNavigationItem[],
  flags: OptionalModuleFlags,
): PortalNavigationItem[] {
  return items
    .filter((item) => item.enabled)
    .map((item) => ({
      ...item,
      children: projectPortalNavigation(item.children ?? [], flags),
    }))
    .filter((item) => {
      if (item.link_type === 'group') return item.children.length > 0
      return isModuleVisible(item, flags)
    })
    .sort((left, right) => left.sort - right.sort)
}
